import React, { useState } from 'react';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import {
  CheckCircle2,
  FileText,
  Play,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Card } from '@/components/ui/card';

// Component to highlight important points
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-6 rounded-r">
      <div className="flex items-start">
        <CheckCircle2 className="h-6 w-6 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-blue-900">{children}</div>
      </div>
    </div>
  );
};

// Diagram container styled like other lectures
const ScientificDiagram = ({
  title,
  caption,
  children,
}: {
  title: string;
  caption: string;
  children: React.ReactNode;
}) => {
  return (
    <figure className="my-8 bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500 mt-1">{caption}</p>
      </div>
      <div className="overflow-hidden">{children}</div>
    </figure>
  );
};

// Video component with the embedded Descript video
const VideoPlayer = () => {
  return (
    <div className="w-full aspect-video rounded-lg overflow-hidden mb-8">
      <iframe 
        src="https://share.descript.com/embed/SVV5adTRAAl" 
        width="100%" 
        height="100%" 
        frameBorder="0" 
        allowFullScreen
        className="rounded-lg"
      ></iframe>
    </div>
  );
};

interface Slide {
  id: number;
  title: string;
  content: React.ReactNode;
}

const Slideshow = ({ slides }: { slides: Slide[] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  return (
    <div className="relative bg-white rounded-lg border border-gray-200 overflow-hidden mb-8">
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-800">
            {slides[currentSlide].title}
          </h3>
          <div className="flex space-x-2">
            <span className="text-sm text-gray-500">
              {currentSlide + 1} / {slides.length}
            </span>
          </div>
        </div>
        <div className="min-h-[300px] py-4">{slides[currentSlide].content}</div>
        <div className="flex justify-between items-center pt-4 border-t border-gray-100">
          <button
            onClick={prevSlide}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ChevronLeft className="h-5 w-5 mr-1" /> Previous
          </button>
          <button
            onClick={nextSlide}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            Next <ChevronRight className="h-5 w-5 ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
};

const lectureSlides: Slide[] = [
  {
    id: 1,
    title: 'The Growing Opportunity',
    content: (
      <>
        <p className="mb-4">
          Longevity medicine attracts entrepreneurs, investors and clinicians alike. As interest grows, you will encounter a variety of partnership requests and business proposals. This lecture offers a framework for protecting your time while evaluating these opportunities.
        </p>
        <KeyTakeaway>
          Recognize the scarcity of experienced longevity clinicians—your expertise is valuable and in high demand.
        </KeyTakeaway>
      </>
    ),
  },
  {
    id: 2,
    title: 'Valuing Your Time',
    content: (
      <>
        <p className="mb-4">
          Start by assigning a real value to your time. Consider your historical earnings and your future earning potential. Every hour spent on a meeting or call is time you could invest in building your own practice or serving existing patients.
        </p>
        <ScientificDiagram
          title="Opportunity Cost"
          caption="Balancing outside requests with your own goals"
        >
          <div className="p-6 grid grid-cols-2 gap-4 text-center">
            <div className="bg-blue-100 p-4 rounded">Grow your clinic</div>
            <div className="bg-green-100 p-4 rounded">Advise external project</div>
          </div>
        </ScientificDiagram>
        <KeyTakeaway>
          Track how external engagements trade off against the progress of your own clinic and personal goals.
        </KeyTakeaway>
      </>
    ),
  },
  {
    id: 3,
    title: 'Filtering Business Requests',
    content: (
      <>
        <ScientificDiagram
          title="Opportunity Filter"
          caption="Decision funnel for incoming proposals"
        >
          <svg viewBox="0 0 400 240" className="w-full" style={{ maxHeight: '240px' }}>
            <rect x="0" y="0" width="400" height="240" fill="#ffffff" />
            <path d="M50 40 L350 40 L275 110 L125 110 Z" fill="#f5f5f5" stroke="#333" />
            <path d="M125 110 L275 110 L240 160 L160 160 Z" fill="#e5e5e5" stroke="#333" />
            <path d="M160 160 L240 160 L220 200 L180 200 Z" fill="#d4d4d4" stroke="#333" />
            <text x="200" y="70" textAnchor="middle" fontSize="14" fill="#333">
              Inquiry
            </text>
            <text x="200" y="125" textAnchor="middle" fontSize="14" fill="#333">
              Alignment
            </text>
            <text x="200" y="170" textAnchor="middle" fontSize="14" fill="#333">
              Value
            </text>
            <text x="200" y="210" textAnchor="middle" fontSize="14" fill="#333">
              Commit
            </text>
          </svg>
        </ScientificDiagram>
        <p className="mb-4">
          Most proposals should exit early if they lack mission alignment or trustworthy partners.
        </p>
      </>
    ),
  },
  {
    id: 4,
    title: 'Prioritizing Requests',
    content: (
      <>
        <p className="mb-4">
          Not every conversation deserves the same priority. Evaluate whether the ask aligns with your objectives or brings clear value. A gym owner sending referrals may be a good fit; a distant colleague requesting free advice might not be.
        </p>
        <KeyTakeaway>
          Use a simple filter: does this opportunity directly support your mission, and is the partner trustworthy?
        </KeyTakeaway>
      </>
    ),
  },
  {
    id: 5,
    title: 'Common Partnership Types',
    content: (
      <>
        <ScientificDiagram
          title="Examples of Business Opportunities"
          caption="Typical requests clinicians encounter"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
            <div className="bg-purple-50 p-4 rounded">Referral relationships</div>
            <div className="bg-yellow-50 p-4 rounded">Vendor discounts & webinars</div>
            <div className="bg-green-50 p-4 rounded">Joint ventures or revenue share</div>
            <div className="bg-red-50 p-4 rounded">Advisory or consulting roles</div>
          </div>
        </ScientificDiagram>
        <p className="mb-4">
          Each category carries different potential benefits and time commitments. Clarify expectations and compensation before agreeing.
        </p>
      </>
    ),
  },
  {
    id: 6,
    title: 'Structuring Deals',
    content: (
      <>
        <p className="mb-4">
          For larger collaborations—such as embedding your services within another business—evaluate the people first. Transparency, references and shared goals matter more than any proposed revenue split.
        </p>
        <p className="mb-4">
          When compensation is offered, cash is often preferable to equity unless you truly believe in the company’s long‑term prospects.
        </p>
        <KeyTakeaway>
          Fair partnerships allow you to capture a reasonable share of the value you help create.
        </KeyTakeaway>
      </>
    ),
  },
  {
    id: 7,
    title: 'Protecting Your Bandwidth',
    content: (
      <>
        <p className="mb-4">
          It is tempting to accept every invitation in hopes that one may lead to a breakthrough opportunity. However, as your schedule fills, small requests add up. Be selective and revisit your priorities regularly.
        </p>
        <KeyTakeaway>
          The most successful clinicians focus their energy where it generates the greatest impact for patients and for their own business.
        </KeyTakeaway>
      </>
    ),
  },
  {
    id: 8,
    title: 'Balancing Time vs Return',
    content: (
      <>
        <ScientificDiagram
          title="Time vs Value"
          caption="Where to focus your energy"
        >
          <svg viewBox="0 0 360 230" className="w-full" style={{ maxHeight: '230px' }}>
            <rect x="0" y="0" width="360" height="230" fill="#ffffff" />
            <line x1="50" y1="20" x2="50" y2="190" stroke="#333" />
            <line x1="50" y1="190" x2="330" y2="190" stroke="#333" />
            <text x="20" y="24" fontSize="12" fill="#333">High</text>
            <text x="20" y="190" fontSize="12" fill="#333">Low</text>
            <text x="335" y="208" fontSize="12" fill="#333">Time Cost</text>
            <rect x="70" y="40" width="100" height="60" fill="#f0f0f0" stroke="#aaa" />
            <text x="120" y="70" fontSize="11" textAnchor="middle" fill="#333">Quick wins</text>
            <rect x="200" y="40" width="100" height="60" fill="#e5e5e5" stroke="#aaa" />
            <text x="250" y="70" fontSize="11" textAnchor="middle" fill="#333">Strategic bets</text>
            <rect x="70" y="120" width="100" height="60" fill="#e0e0e0" stroke="#aaa" />
            <text x="120" y="150" fontSize="11" textAnchor="middle" fill="#333">Low impact</text>
            <rect x="200" y="120" width="100" height="60" fill="#d5d5d5" stroke="#aaa" />
            <text x="250" y="150" fontSize="11" textAnchor="middle" fill="#333">Avoid</text>
          </svg>
        </ScientificDiagram>
        <KeyTakeaway>
          Plotting opportunities helps reveal where small efforts could yield outsized benefits.
        </KeyTakeaway>
      </>
    ),
  },
];

const EvaluatingBusinessOpportunities: React.FC = () => {
  return (
    <MentorshipAccessGate>
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <main className="container mx-auto px-4 py-8 max-w-4xl">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              Evaluating Business Opportunities
            </h1>
            <p className="text-gray-600">
              A framework for assessing partnerships and protecting your time
            </p>
          </div>
          <VideoPlayer />
          <Slideshow slides={lectureSlides} />
        </main>
        <Footer />
      </div>
    </MentorshipAccessGate>
  );
};

export default EvaluatingBusinessOpportunities;
